import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CreationOverlay } from "../misc/CreationOverlay";
import { DialogTitle } from "@/components/ui/dialog";
import {
  CreateBase,
  Form,
  useGetIdentity,
  useListContext,
  useNotify,
  useRecordContext,
  useResourceContext,
  useTranslate,
  useUpdate,
  type Identifier,
  type RaRecord,
} from "ra-core";
import { useFormContext } from "react-hook-form";
import { SaveButton } from "@/components/admin/form";
import { cn } from "@/lib/utils";

import { NoteInputs } from "./NoteInputs";
import { getCurrentDate } from "./utils";
import { foreignKeyMapping } from "./foreignKeyMapping";

export const NoteCreate = ({
  reference,
  showStatus,
  className,
}: {
  reference: "contacts" | "deals";
  showStatus?: boolean;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  const resource = useResourceContext();
  const record = useRecordContext();
  const { identity } = useGetIdentity();

  if (!record || !identity) return null;

  const defaultStatus = reference === "contacts" ? record.status : undefined;

  const form = (
    <CreateBase resource={resource} redirect={false}>
      <Form>
        <div className={cn("space-y-3", className)}>
          <NoteInputs
            defaultStatus={defaultStatus}
            showStatus={showStatus}
            expanded
          />
          <NoteCreateButton
            defaultStatus={defaultStatus}
            record={record}
            reference={reference}
            onSaved={() => setOpen(false)}
          />
        </div>
      </Form>
    </CreateBase>
  );
  return (
    <>
      <Button type="button" onClick={() => setOpen(true)}>
        Добавить заметку
      </Button>
      <CreationOverlay open={open} onOpenChange={setOpen}>
        <div className="shrink-0 border-b p-4 pr-12">
          <DialogTitle>Новая заметка и файлы</DialogTitle>
        </div>
        <div className="min-h-0 overflow-y-auto flex-1 p-4">{form}</div>
      </CreationOverlay>
    </>
  );
};

const NoteCreateButton = ({
  defaultStatus,
  reference,
  record,
  onSaved,
}: {
  onSaved?: () => void;
  defaultStatus?: string;
  reference: "contacts" | "deals";
  record: RaRecord<Identifier>;
}) => {
  const [update] = useUpdate();
  const notify = useNotify();
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { reset } = useFormContext();
  const { refetch } = useListContext();

  if (!record || !identity) return null;

  const resetValues: {
    date: string;
    text: null;
    attachments: null;
    status?: string;
  } = {
    date: getCurrentDate(),
    text: null,
    attachments: null,
  };

  const handleSuccess = (data: any) => {
    if (reference === "contacts") {
      resetValues.status = data.status ?? defaultStatus;
    }

    reset(resetValues, { keepValues: false });
    refetch();
    onSaved?.();
    if (reference === "contacts")
      update(reference, {
        id: (record && record.id) as unknown as Identifier,
        data: {
          last_seen:
            reference === "contacts" ? new Date().toISOString() : undefined,
          status: data.status,
        },
        previousData: record,
      });
    notify("resources.notes.added", {
      messageArgs: {
        _: "Note added",
      },
    });
  };

  return (
    <div className="flex justify-end">
      <SaveButton
        type="button"
        label={translate("resources.notes.action.add_this")}
        transform={(data) => ({
          ...data,
          [foreignKeyMapping[reference]]: record.id,
          sales_id: identity.id,
          date: new Date(data.date || getCurrentDate()).toISOString(),
        })}
        mutationOptions={{
          onSuccess: handleSuccess,
        }}
      />
    </div>
  );
};
